/**
 * OralHabitsCard — орални навици и лицево развитие.
 * Данни: GET/POST /api/dental-intelligence/oral-habits
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Activity, Info, ArrowRight } from "lucide-react";
import { useAuth } from "@/App";

const LEVEL = {
  low: "#10B981", moderate: "#F59E0B", high: "#EF4444",
};

export default function OralHabitsCard({ isDark = false, cardCls = "" }) {
  const { API } = useAuth();
  const [d, setD] = useState(null);
  const [subj, setSubj] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dental-intelligence/oral-habits`);
      setD(data);
    } catch { setD(null); }
  }, [API]);

  useEffect(() => { load(); }, [load]);
  if (!d) return null;

  const border = isDark ? "border-white/10" : "border-gray-200";
  const s = d.subjects[subj] || d.subjects[0];
  const current = Object.fromEntries((s.assessments || []).map((a) => [a.key, a.intensity]));

  const set = async (key, intensity) => {
    const habits = { ...current };
    if (habits[key] === intensity) delete habits[key];
    else habits[key] = intensity;
    try {
      const { data } = await axios.post(`${API}/dental-intelligence/oral-habits`, {
        child_id: s.subject_id, habits,
      });
      setD(data);
    } catch { toast.error("Не се записа"); }
  };

  return (
    <div className={cardCls || `p-4 rounded-2xl border ${border}`} data-testid="dhi-habits-card">
      <div className="text-sm font-bold inline-flex items-center gap-1.5">
        <Activity className="w-4 h-4 text-cyan-500" /> Орални навици и лицево развитие
      </div>
      <p className="text-[11px] opacity-70 mt-0.5">{d.intro_bg}</p>

      {d.subjects.length > 1 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="dhi-habits-subjects">
          {d.subjects.map((x, i) => (
            <button key={i} onClick={() => setSubj(i)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-bold border
                ${i === subj ? "border-cyan-500 bg-cyan-500/15 text-cyan-600" : `${border} opacity-70`}`}
              data-testid={`dhi-habits-subject-${i}`}>
              {x.name}
            </button>
          ))}
        </div>
      )}

      <div className="mt-3 space-y-2">
        {d.catalog.map((h) => {
          const a = (s.assessments || []).find((x) => x.key === h.key);
          return (
            <div key={h.key} className={`p-2.5 rounded-xl border ${border}`}
              data-testid={`dhi-habit-${h.key}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-[12px] font-bold">{h.label_bg}</span>
                <div className="flex gap-1">
                  {d.intensities.map((it) => (
                    <button key={it.key} onClick={() => set(h.key, it.key)}
                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold border transition
                        ${current[h.key] === it.key
                          ? "border-cyan-500 bg-cyan-500/15 text-cyan-600" : `${border} opacity-60`}`}
                      data-testid={`dhi-habit-${h.key}-${it.key}`}>
                      {it.label_bg}
                    </button>
                  ))}
                </div>
              </div>
              {a && (
                <div className="mt-1.5 text-[11px]" data-testid={`dhi-habit-assess-${h.key}`}>
                  <span className="font-bold" style={{ color: LEVEL[a.level] }}>
                    {a.level_bg}
                  </span>
                  <span className="opacity-80"> — {a.action_bg}</span>
                  <div className="opacity-65 mt-0.5">{a.facts_bg}</div>
                  <div className="opacity-65 mt-0.5">
                    Влияе на: {a.effects_bg.join(" · ")}
                  </div>
                  {a.referral_bg && (
                    <div className="mt-0.5 inline-flex items-center gap-1 font-bold text-cyan-600">
                      <ArrowRight className="w-3 h-3" /> Към: {a.referral_bg}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className={`mt-3 p-2.5 rounded-xl border ${border}`} data-testid="dhi-habits-strategies">
        <div className="text-[10px] font-bold uppercase opacity-60 mb-1">
          Как се спира навик
        </div>
        {d.strategies_bg.map((t, i) => (
          <div key={i} className="text-[11px] flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-cyan-500" /> {t}
          </div>
        ))}
      </div>

      <div className="text-[9px] opacity-50 mt-2">{d.note_bg}</div>
    </div>
  );
}
